import { useNavigate, useRouterState, Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { Bell, Menu, PanelLeft, LogOut, ChevronDown, ExternalLink, Check } from "lucide-react";
import { useAuth, ROLE_LABEL } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { notificationsApi, type NotificationDto } from "@/lib/api";
import { toast } from "sonner";

const ROUTE_TITLES: Record<string, string> = {
  "/admin": "Dashboard",
  "/admin/dashboard": "Dashboard",
  "/admin/bookings": "Bookings",
  "/admin/venues": "Venues",
  "/admin/listings": "My listings",
  "/admin/rooms": "Room Types",
  "/admin/calendar": "Calendar",
  "/admin/customers": "Customers",
  "/admin/staff": "Staff Users",
  "/admin/analytics": "Analytics",
  "/admin/support": "Support",
  "/admin/settings": "Settings",
};

export function AdminHeader({
  onToggleSidebar,
  onOpenMobile,
}: {
  onToggleSidebar: () => void;
  onOpenMobile: () => void;
}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { user, role, logout } = useAuth();
  const navigate = useNavigate();

  const title = ROUTE_TITLES[pathname] ?? "Dashboard";

  const [notifs, setNotifs] = useState<NotificationDto[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await notificationsApi.list(1, 20);
        if (!cancelled) {
          setNotifs(res.items.slice(0, 10));
          setUnreadCount(res.unreadCount);
        }
      } catch {
        // silently fail — bell stays empty
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  async function handleMarkRead(id: string) {
    try {
      await notificationsApi.markRead(id);
      setNotifs(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n));
      setUnreadCount(c => Math.max(0, c - 1));
    } catch {
      toast.error("Failed to mark notification as read");
    }
  }

  return (
    <header className="sticky top-0 z-30 flex h-20 items-center gap-3 border-b border-border/60 bg-background/85 px-4 backdrop-blur sm:px-6">
      <button
        onClick={onOpenMobile}
        className="grid size-9 place-items-center rounded-md hover:bg-accent lg:hidden"
        aria-label="Open menu"
      >
        <Menu className="size-6" />
      </button>
      <button
        onClick={onToggleSidebar}
        className="hidden size-9 place-items-center rounded-md hover:bg-accent lg:grid"
        aria-label="Toggle sidebar"
      >
        <PanelLeft className="size-6" />
      </button>

      <div className="hidden flex-col sm:flex">
        <h1 className="font-display text-lg font-semibold leading-tight">{title}</h1>
        <p className="text-xs text-muted-foreground">OyoBooking</p>
      </div>

      <div className="ml-auto flex items-center gap-2">
        <DropdownMenu open={notifOpen} onOpenChange={setNotifOpen}>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="size-9 relative">
              <Bell className="size-5" />
              {unreadCount > 0 && (
                <span className="absolute right-1.5 top-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-white">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80 max-h-[360px] overflow-y-auto">
            <DropdownMenuLabel className="flex items-center justify-between">
              <span className="font-semibold text-sm">Notifications</span>
              {unreadCount > 0 && (
                <span className="text-[10px] font-medium bg-destructive/10 text-destructive rounded-full px-2 py-0.5">
                  {unreadCount} unread
                </span>
              )}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {notifs.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                No notifications yet
              </div>
            ) : (
              notifs.map(n => (
                <DropdownMenuItem
                  key={n.id}
                  className={`flex items-start gap-3 p-3 cursor-pointer ${n.isRead ? "opacity-60" : "bg-accent/30"}`}
                  onClick={() => {
                    if (!n.isRead) handleMarkRead(n.id);
                    if (n.actionUrl) navigate({ to: n.actionUrl });
                    setNotifOpen(false);
                  }}
                >
                  <div className="mt-0.5 shrink-0">
                    {n.isRead ? (
                      <Check className="size-4 text-muted-foreground" />
                    ) : (
                      <span className="block size-2 rounded-full bg-primary" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium leading-tight">{n.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.message}</p>
                    <p className="text-[10px] text-muted-foreground/60 mt-1">
                      {new Date(n.createdAt).toLocaleString()}
                    </p>
                  </div>
                </DropdownMenuItem>
              ))
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2 rounded-full border border-border/60 px-2 py-1 hover:bg-accent">
              <span className="grid size-8 place-items-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
                {(user?.name ?? "A").slice(0, 1).toUpperCase()}
              </span>
              <div className="hidden flex-col items-start sm:flex">
                <span className="text-sm font-semibold leading-tight">{user?.name}</span>
                <span className="text-xs text-muted-foreground leading-tight">
                  {role ? ROLE_LABEL[role] : "Member"}
                </span>
              </div>
              <ChevronDown className="size-4 text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col gap-0.5">
                <span className="font-semibold text-sm">{user?.name}</span>
                <span className="text-xs text-muted-foreground">{user?.email}</span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/" className="flex items-center gap-2">
                <ExternalLink className="size-4" /> Back to website
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => {
                logout();
                navigate({ to: "/login" });
              }}
              className="text-destructive focus:text-destructive"
            >
              <LogOut className="mr-2 size-4" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
